"use client"

import { Button } from '../../../shared/components/ui/button'
import { SlidersHorizontal } from 'lucide-react'
import React, { useMemo, useState } from 'react'
import { useIsMobile } from '../../../shared/hooks/use-mobile'
import { FormProvider, useForm } from 'react-hook-form'
import { roleFilterOptions, statusFilterWithAllOptions } from '../constants/options'
import { usePathname, useRouter } from 'next/navigation'
import { buildUrl } from '../../../shared/lib/utils'
import { DASHBOARD_ROUTE } from '../../../shared/constants/pages-routes'
import { Dialog, DialogTitle, DialogContent, DialogTrigger, DialogClose } from '../../../shared/components/ui/dialog'
import { useCurrentSearchParams } from '../../../shared/hooks/use-current-search-params'
import { VolunteerQueryParamsType, volunteerQueryParamsWithoutPaginationSchema } from '../schemas/volunteer-query.schema'
import { DialogDescription } from '@radix-ui/react-dialog'
import FormMultiSelectInput from '../../../shared/components/form-inputs/form-multi-select-input'
import FormDateInput from '../../../shared/components/form-inputs/form-date-input'
import FormOrganizationInput from '../../../shared/components/form-inputs/form-organization-input'
import FormSelectInput from '../../../shared/components/form-inputs/form-select-input'
import { useQueryClient } from '@tanstack/react-query'
import { ADMIN } from '@/shared/constants/backend-api'
import { SEARCH_PARAMS_TAB_KEY } from '../constants/volunteer-tab-links'

type VolunteerFilterSearchParams = VolunteerQueryParamsType & {
    [SEARCH_PARAMS_TAB_KEY]?: string | null
}

type VolunteerFilterFormValues = Omit<
    VolunteerQueryParamsType,
    | 'arrivalDate'
    | 'departureDate'
    | 'addedFrom'
    | 'addedTo'
    | 'lastActiveAtFrom'
    | 'lastActiveAtTo'
    | 'status'
    | 'limit'
    | 'page'
> & {
    arrivalDate?: Date | string
    departureDate?: Date | string
    addedFrom?: Date | string
    addedTo?: Date | string
    lastActiveAtFrom?: Date | string
    lastActiveAtTo?: Date | string
    status?: string // 'all' | 'active' | 'inactive'
}

function parseDateForForm(dateStr: string | null | undefined): Date | undefined {
    if (!dateStr) return undefined
    try {
        const str = dateStr.split('T')[0]
        if (!/^\d{4}-\d{2}-\d{2}$/.test(str)) return undefined
        const [year, month, day] = str.split('-').map(Number)
        return new Date(year, month - 1, day)
    } catch {
        return undefined
    }
}

function dateToYyyyMmDd(val: Date | string | undefined): string | undefined {
    if (!val) return undefined
    if (val instanceof Date) {
        const y = val.getFullYear()
        const m = String(val.getMonth() + 1).padStart(2, '0')
        const d = String(val.getDate()).padStart(2, '0')
        return `${y}-${m}-${d}`
    }
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) return val.slice(0, 10)
    return undefined
}

function buildFilterFormValues(input: {
    roles: string[]
    arrivalDate?: string | null
    departureDate?: string | null
    organization?: string | null
    addedFrom?: string | null
    addedTo?: string | null
    lastActiveAtFrom?: string | null
    lastActiveAtTo?: string | null
    statusForForm: string
}): VolunteerFilterFormValues {
    return {
        roles: input.roles,
        arrivalDate: parseDateForForm(input.arrivalDate),
        departureDate: parseDateForForm(input.departureDate),
        organization: input.organization ?? undefined,
        addedFrom: parseDateForForm(input.addedFrom),
        addedTo: parseDateForForm(input.addedTo),
        lastActiveAtFrom: parseDateForForm(input.lastActiveAtFrom),
        lastActiveAtTo: parseDateForForm(input.lastActiveAtTo),
        status: input.statusForForm,
    }
}

function VolunteerFilterButton() {
    const isMobile = useIsMobile()
    const router = useRouter()
    const pathname = usePathname()
    const queryClient = useQueryClient()
    const previousParams = useCurrentSearchParams<VolunteerFilterSearchParams>([
        "limit",
        SEARCH_PARAMS_TAB_KEY,
        "roles",
        "arrivalDate",
        "departureDate",
        "organization",
        "addedFrom",
        "addedTo",
        "lastActiveAtFrom",
        "lastActiveAtTo",
        "status",
    ])

    // buildUrl expects undefined (not null) for omitted values
    const sanitizedPreviousParams = useMemo(() => previousParams && Object.fromEntries(
        Object.entries(previousParams).map(([k, v]) => [k, v === null ? undefined : v])
    ), [previousParams])

    const normalizedParams = {
        ...previousParams,
        roles: Array.isArray(previousParams.roles)
            ? previousParams.roles
            : previousParams.roles
                ? [previousParams.roles]
                : []
    }

    const parsedPreviousParams = volunteerQueryParamsWithoutPaginationSchema.safeParse(normalizedParams)
    const parsed = parsedPreviousParams.success ? parsedPreviousParams.data : null
    const roles = parsed?.roles || []
    const arrivalDate = parsed?.arrivalDate
    const departureDate = parsed?.departureDate
    const organization = parsed?.organization
    const addedFrom = parsed?.addedFrom
    const addedTo = parsed?.addedTo
    const lastActiveAtFrom = parsed?.lastActiveAtFrom
    const lastActiveAtTo = parsed?.lastActiveAtTo
    const statusParam = parsed?.status // boolean | undefined from URL

    const [isOpen, setIsOpen] = useState(false)

    const statusForForm = statusParam === true ? 'active' : statusParam === false ? 'inactive' : 'all'

    const formValuesFromUrl = useMemo(
        () =>
            buildFilterFormValues({
                roles,
                arrivalDate,
                departureDate,
                organization,
                addedFrom,
                addedTo,
                lastActiveAtFrom,
                lastActiveAtTo,
                statusForForm,
            }),
        [
            roles,
            arrivalDate,
            departureDate,
            organization,
            addedFrom,
            addedTo,
            lastActiveAtFrom,
            lastActiveAtTo,
            statusForForm,
        ]
    )

    const form = useForm<VolunteerFilterFormValues>({
        defaultValues: formValuesFromUrl,
    })

    const { isDirty, isSubmitting } = form.formState
    const { control, watch } = form

    const currentRoles = watch('roles') || []
    const currentArrivalDate = watch('arrivalDate')
    const currentDepartureDate = watch('departureDate')
    const currentOrganization = watch('organization')
    const currentAddedFrom = watch('addedFrom')
    const currentAddedTo = watch('addedTo')
    const currentLastActiveAtFrom = watch('lastActiveAtFrom')
    const currentLastActiveAtTo = watch('lastActiveAtTo')
    const currentStatus = watch('status')

    const hasActiveFilters =
        currentRoles.length > 0 ||
        !!currentArrivalDate ||
        !!currentDepartureDate ||
        !!currentOrganization ||
        !!currentAddedFrom ||
        !!currentAddedTo ||
        !!currentLastActiveAtFrom ||
        !!currentLastActiveAtTo ||
        (currentStatus && currentStatus !== 'all')

    const handleOpenChange = (open: boolean) => {
        setIsOpen(open)
        // Always sync from the current URL when opening so chip clears are reflected.
        if (open) {
            form.reset(formValuesFromUrl)
        }
    }

    const handleSubmit = (data: VolunteerFilterFormValues) => {
        const arrivalDateString = dateToYyyyMmDd(data.arrivalDate)
        const departureDateString = dateToYyyyMmDd(data.departureDate)
        const addedFromString = dateToYyyyMmDd(data.addedFrom)
        const addedToString = dateToYyyyMmDd(data.addedTo)
        const lastActiveAtFromString = dateToYyyyMmDd(data.lastActiveAtFrom)
        const lastActiveAtToString = dateToYyyyMmDd(data.lastActiveAtTo)
        const statusUrl = data.status === 'all' ? undefined : data.status === 'active' ? 'true' : 'false'

        const url = buildUrl(
            pathname || DASHBOARD_ROUTE.volunteers,
            {
                roles: data.roles,
                arrivalDate: arrivalDateString,
                departureDate: departureDateString,
                organization: data.organization,
                addedFrom: addedFromString,
                addedTo: addedToString,
                lastActiveAtFrom: lastActiveAtFromString,
                lastActiveAtTo: lastActiveAtToString,
                status: statusUrl,
            },
            { previousParams: sanitizedPreviousParams }
        )
        router.push(url)

        queryClient.invalidateQueries({ queryKey: [ADMIN.tableRows] })

        setIsOpen(false)
        form.reset(data)
    }

    const handleClearAll = () => {
        const clearedValues: VolunteerFilterFormValues = {
            roles: [],
            arrivalDate: undefined,
            departureDate: undefined,
            organization: undefined,
            addedFrom: undefined,
            addedTo: undefined,
            lastActiveAtFrom: undefined,
            lastActiveAtTo: undefined,
            status: 'all',
        }
        const url = buildUrl(
            pathname || DASHBOARD_ROUTE.volunteers,
            {
                roles: undefined,
                arrivalDate: undefined,
                departureDate: undefined,
                organization: undefined,
                addedFrom: undefined,
                addedTo: undefined,
                lastActiveAtFrom: undefined,
                lastActiveAtTo: undefined,
                status: undefined,
            },
            { previousParams: sanitizedPreviousParams }
        )
        router.push(url)

        queryClient.invalidateQueries({ queryKey: [ADMIN.tableRows] })

        form.reset(clearedValues)
        setIsOpen(false)
    }

    return (
        <Dialog open={isOpen} onOpenChange={handleOpenChange}>
            <DialogTrigger asChild>
                <Button
                    variant={"default"}
                    size={"sm"}
                    className='bg-primary hover:bg-primary/90 text-white font-bold rounded-md gap-2 py-1 h-10'
                >
                    <SlidersHorizontal className="w-5 h-5" />
                    {!isMobile && <span>Filter</span>}
                </Button>
            </DialogTrigger>
            <DialogContent className="flex max-h-[90vh] max-w-lg flex-col gap-4 overflow-hidden">
                <DialogTitle className="shrink-0">
                    Filters
                </DialogTitle>
                <DialogDescription className='hidden'>
                    Filter volunteers by status and location
                </DialogDescription>
                <FormProvider {...form}>
                    <form
                        onSubmit={form.handleSubmit(handleSubmit)}
                        className='flex min-h-0 flex-1 flex-col gap-3'
                    >
                        <div
                            className="max-h-[calc(90vh-10rem)] space-y-3 overflow-y-auto overflow-x-hidden overscroll-contain pr-1 -mr-1"
                            style={{ WebkitOverflowScrolling: 'touch' }}
                        >
                            <FormMultiSelectInput
                                name='roles'
                                options={roleFilterOptions}
                                placeholder='Select roles'
                                inputGroupProps={{
                                    label: 'Roles',
                                }}
                                control={control}
                            />
                            <FormOrganizationInput
                                name='organization'
                                placeholder='Search or type organization name...'
                                inputGroupProps={{
                                    label: 'Organization/Church/Individual',
                                }}
                                control={control}
                            />
                            <FormDateInput
                                name='arrivalDate'
                                placeholder='Select serve date'
                                inputGroupProps={{
                                    label: 'Serve Date',
                                }}
                                control={control}
                                enableYearMonthDropdowns={true}
                                clearable
                            />
                            <FormSelectInput
                                name='status'
                                control={control}
                                options={statusFilterWithAllOptions}
                                placeholder='Select status'
                                inputGroupProps={{
                                    label: 'Status',
                                }}
                            />
                            <FormDateInput
                                name='departureDate'
                                placeholder='Select departure date'
                                inputGroupProps={{
                                    label: 'Departure Date',
                                }}
                                control={control}
                                enableYearMonthDropdowns={true}
                                clearable
                            />
                            <FormDateInput
                                name='addedFrom'
                                placeholder='Select from date'
                                inputGroupProps={{
                                    label: 'Added (from)',
                                }}
                                control={control}
                                enableYearMonthDropdowns={true}
                                clearable
                            />
                            <FormDateInput
                                name='addedTo'
                                placeholder='Select to date'
                                inputGroupProps={{
                                    label: 'Added (to)',
                                }}
                                control={control}
                                enableYearMonthDropdowns={true}
                                clearable
                            />
                            <FormDateInput
                                name='lastActiveAtFrom'
                                placeholder='Select from date'
                                inputGroupProps={{
                                    label: 'Last Active (from)',
                                }}
                                control={control}
                                enableYearMonthDropdowns={true}
                                clearable
                            />
                            <FormDateInput
                                name='lastActiveAtTo'
                                placeholder='Select to date'
                                inputGroupProps={{
                                    label: 'Last Active (to)',
                                }}
                                control={control}
                                enableYearMonthDropdowns={true}
                                clearable
                            />
                        </div>
                        <div className='shrink-0 space-y-2 border-t pt-3'>
                            {hasActiveFilters && (
                                <Button
                                    type='button'
                                    variant='ghost'
                                    className='w-full text-muted-foreground'
                                    onClick={handleClearAll}
                                >
                                    Clear all filters
                                </Button>
                            )}
                            <div className='grid grid-cols-2 gap-2'>
                                <DialogClose asChild>
                                    <Button
                                        variant={"outline"}
                                        className='w-full'
                                        type='button'
                                    >
                                        Cancel
                                    </Button>
                                </DialogClose>
                                <Button
                                    type='submit'
                                    className='w-full'
                                    disabled={isSubmitting || (!isDirty && !hasActiveFilters)}
                                    showLoader={isSubmitting}
                                >
                                    Apply
                                </Button>
                            </div>
                        </div>
                    </form>
                </FormProvider>
            </DialogContent>
        </Dialog>
    )
}

export default VolunteerFilterButton
